import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Badge, buttonClass, Spinner } from "../../ui/index.js";
import { fmtDateTime, fmtMoney, useCabinet } from "../cabinet/index.js";
import { instagramLink, statusTone } from "../calendar/index.js";

/**
 * Карточка клиента рядом с перепиской на широком экране: контакты, неявки, заметки, последние визиты
 * и ссылка на полную карточку. На телефоне карточка открывается отдельной страницей.
 */
export function ClientPanel({ clientId }: { clientId: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cab = useCabinet();
  const card = useQuery(trpc.calendar.clients.card.queryOptions({ clientId }));
  const extra = useQuery(trpc.cabinet.client.queryOptions({ clientId }));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId, includeInactive: true }));
  const client = card.data?.client;
  const instagram = extra.data?.brief.instagram ?? null;
  const masterName = (id: string) => masters.data?.find((m) => m.id === id)?.name ?? "";

  return (
    <aside aria-label={t("conv.client")} className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto rounded-card border border-line bg-white p-4">
      {card.isPending && <Spinner />}
      {client && (
        <>
          <div>
            <div className="text-xs text-ink-3">{t("conv.client")}</div>
            <div className="truncate text-lg font-semibold">{client.name || (instagram ? `@${instagram}` : t("clients.noName"))}</div>
            {client.noShowCount > 0 && (
              <div className="mt-1">
                <Badge tone="red">{t("clients.noShows", { count: client.noShowCount })}</Badge>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {instagram && (
              <a href={instagramLink(instagram)} target="_blank" rel="noreferrer" className={buttonClass("secondary", "", "sm")}>
                @{instagram}
              </a>
            )}
            {client.phone && (
              <a href={`tel:${client.phone}`} className={buttonClass("secondary", "", "sm")}>
                {client.phone}
              </a>
            )}
          </div>
          {client.notes && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-3">{t("clients.notes")}</div>
              <div className="whitespace-pre-wrap rounded-field bg-slate-50 p-3 text-sm">{client.notes}</div>
            </div>
          )}
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-3">{t("clients.visits")}</div>
            {card.data?.visits.length === 0 && <div className="text-sm text-ink-3">{t("clients.noVisits")}</div>}
            <div className="space-y-3">
              {card.data?.visits.slice(0, 5).map((v) => (
                <div key={v.id} className="rounded-field border border-line px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{fmtDateTime(v.period.start, cab.timezone, lang)}</span>
                    <Badge tone={statusTone[v.status]} className="whitespace-nowrap">
                      {t(`cal.status.${v.status}`)}
                    </Badge>
                  </div>
                  <div className="text-ink-3">
                    {v.items.map((i) => i.name).join(" + ")} · {masterName(v.masterId)} ·{" "}
                    {fmtMoney(
                      v.items.reduce((s, i) => s + i.price.amount, 0),
                      lang,
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Link to={`${cab.base}/clients/${client.id}`} className={buttonClass("ghost", "mt-auto", "sm")}>
            {t("conv.openCard")}
          </Link>
        </>
      )}
    </aside>
  );
}
